import React, { useCallback, useMemo } from "react";
import {
  Autocomplete,
  Button,
  Grid2 as Grid,
  IconButton,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ListPageHeader, { ListPageHeaderLeft } from "../../../../../Components/Misc/ListPageHeader";
import { assetService, cacheService, titleService } from "../../../../../Core/Api/services";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { ModalContext } from "../../../../../Components/Modals";
import useCustomSnackbar from "../../../../../Utils/Hooks/useCustomSnackbar";
import TableCellBool from "../../../../../Components/Table/TableCellBool";
import FormSection from "../../../../../Components/Forms/FormSection/form-section";
import { DbMetadataFieldInfos } from "../../../../../Core/Api/Types/db-metadata-field-infos";
import { produce } from "immer";
import ModalTmdbSearch, {
  ModalTmdbSearchElementProps,
} from "../../../../../Components/Modals/ModalTmdbSearch/modal-tmdb-search";
import RemoveIcon from "@mui/icons-material/Remove";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import moment from "moment";
import { BaseMetadataType } from "../../../../../Core/Api/Types/base-metadata-type";
import { DbAssetObjectInfos } from "../../../../../Core/Api/Types/db-asset-object-infos";
import AutocompleteSearchCatalog from "../../../../../Components/Search/AutocompleteSearchCatalog/autocomplete-search-catalog";
import { GetDetailedTmdbTitleRequest } from "../../../../../Core/Api/Types/get-detailed-tmdb-title-request";
import { PlatformLanguageCode } from "../../../../../Core/Api/Types/platform-language-code";
import { TitleType } from "../../../../../Core/Api/Types/title-type";
import { CastMetadataInfos } from "../../../../../Core/Api/Types/cast-metadata-infos";
import { useTranslation } from "react-i18next";
import MetadataEditor from "../../../../../Components/MetadataEditor/metadata-editor";
import useMetadataForm from "../../../../../Utils/Hooks/useMetadataForm";

const createLanguageCodeValue = (value: PlatformLanguageCode | null) => {
  if (!value) return null;
  return {
    externalId: value?.externalId,
    id: value?.id,
    label: value?.label,
    value: value?.value,
    valueObject: value?.valueObject,
    rfc5646: null,
    $id: null,
    $type: "VDM.AirLab.Sdk.Core.Types.CommonTypes.PlatformLanguageCode, VDM.AirLab.Sdk.Core",
  };
};

function InventoryCreateTitle() {
  const { t } = useTranslation();
  const customerCode = cacheService.GetCustomerExternalIdLabel();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const [context, setContext] = React.useState<"CREATE" | "UPDATE">("CREATE");
  const [fields, setFields] = React.useState<DbMetadataFieldInfos[]>([]);
  const [titleTypes, setTitleTypes] = React.useState<any[]>([]);
  const [selectedType, setSelectedType] = React.useState<TitleType | null>(null);
  const [pattern, setPattern] = React.useState<string>("");
  const [seasonNb, setSeasonNb] = React.useState<string>("");
  const [customerId, setCustomerId] = React.useState<string>("");
  const [newTitle, setNewTitle] = React.useState<string>("");
  const [episodesNumb, setEpisodesNumb] = React.useState<string>("");
  const [internalTitle, setInternalTitle] = React.useState<DbAssetObjectInfos>();
  const [currentTitleInfos, setCurrentTitleInfos] = React.useState<{
    languageCode: PlatformLanguageCode | null;
    text: string | null;
  }>({ languageCode: null, text: null });

  const [titleInfos, setTitleInfos] = React.useState<
    { languageCode: PlatformLanguageCode | null; text: string | null }[]
  >([]);
  const [currentSynopInfos, setCurrentSynopInfos] = React.useState<{
    dataType: number | null;
    languageCode: PlatformLanguageCode | null;
    text: string | null;
  }>({ dataType: null, languageCode: null, text: null });
  const [synopInfos, setSynopInfos] = React.useState<
    { dataType: number | null; languageCode: PlatformLanguageCode | null; text: string | null }[]
  >([]);
  const [currentCastsInfos, setCurrentCastsInfos] = React.useState<{
    function: string | null;
    name: string | null;
    lastname: string | null;
    order: number | null;
  }>({ function: null, name: null, lastname: null, order: null });
  const [castsInfos, setCastsInfos] = React.useState<
    { function: string | null; name: string | null; lastname: string | null; order: number | null }[]
  >([]);
  const { formValid, handleMetadataChanges, metadataListFilled, setMetadataListFilled } = useMetadataForm();

  // Getting metadatas
  React.useEffect(() => {
    titleService.GetMetadataFields().then((response) => {
      setTitleTypes(response.metadataFields.Title.find((x) => x.tagName === "titleType")?.values || []);
      setFields(
        response.metadataFields.Title.filter((x) => x.isEditable && !x.baseType?.includes("Localized")).sort(function (
          x,
          y
        ) {
          // true values first
          return x.isMandatory === y.isMandatory ? 0 : x ? -1 : 1;
        })
      );
    });
  }, []);

  const handleNotBaseMetadata = useCallback(
    (metas: any) => {
      if (titleInfos.length > 0)
        metas.push({
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbTitlesMetadataValueInfos, VDM.AirLab.Sdk.Core",
          fieldType: BaseMetadataType.DbTitlesMetadataValueInfos,
          fieldName: "titles",
          fieldLabel: "Titles",
          creationTime: moment().format("YYYY-MM-DD"),
          value: titleInfos,
          id: fields.find((x) => x.tagName === "titles")?.id,
        });

      if (synopInfos.length > 0)
        metas.push({
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbTitlesMetadataValueInfos, VDM.AirLab.Sdk.Core",
          fieldType: BaseMetadataType.DbSynopsesMetadataValueInfos,
          fieldName: "synopses",
          fieldLabel: "Synopses",
          creationTime: moment().format("YYYY-MM-DD"),
          value: synopInfos,
          id: fields.find((x) => x.tagName === "synopses")?.id,
        });

      if (castsInfos.length > 0) {
        let val: CastMetadataInfos[] = [];

        castsInfos.forEach((castInfos) => {
          val.push(
            new CastMetadataInfos({
              $type: "VDM.AirLab.Sdk.Core.Types.AssetTypes.CastMetadataInfos, VDM.AirLab.Sdk.Core",
              order: castInfos.order,
              function: castInfos.function,
              name: castInfos.name,
              lastname: castInfos.lastname,
            })
          );
        });

        metas.push({
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbCastsMetadataValueInfos, VDM.AirLab.Sdk.Core",
          fieldType: BaseMetadataType.DbCastsMetadataValueInfos,
          fieldName: "casts",
          fieldLabel: "Casts",
          creationTime: moment().format("YYYY-MM-DD"),
          value: val,
          id: fields.find((x) => x.tagName === "casts")?.id,
        });
      }
    },
    [castsInfos, fields, synopInfos, titleInfos]
  );

  const handleConfirmClick = () => {
    handleSubmit();
  };

  const handleTmdbSearch = () => {
    if (!openModal) return;
    openModal<ModalTmdbSearchElementProps>(ModalTmdbSearch, {
      name: pattern,
      type: selectedType!,
      onSelect: getDetails,
    });
  };

  const getDetails = (movie) => {
    var postData: GetDetailedTmdbTitleRequest = {
      tmdbId: movie.id,
      type: fields.find((x) => x.tagName === "titleType")?.value,
      seasonNumber: seasonNb ? parseInt(seasonNb) : null,
      titleId: null,
    };

    titleService.GetDetailedTmdbTitle(postData).then((response) => {
      if (response.title) {
        let title = response.title;
        let metas = response.title.metadata;
        setPattern(title.name);
        setMetas(metas);
      }
    });
  };

  const handleInternalTitlePick = (title: DbAssetObjectInfos) => {
    setContext("UPDATE");

    assetService.GetTitleInfosByObjectId(title.id).then(function (response) {
      if (response.success) {
        let title = response.graph;
        setInternalTitle(response.graph!);
        let metas = response.graph?.metadata;
        setPattern(title!.name);
        setSelectedType(title?.metadata?.find((x) => x.fieldName === "titleType")?.value);
        setCustomerId(title!.titleCustomerId!);
        if (title?.metadata?.find((x) => x.fieldName === "serieSeasonNumber")?.value)
          setSeasonNb(title?.metadata?.find((x) => x.fieldName === "serieSeasonNumber")?.value);
        if (title?.metadata?.find((x) => x.fieldName === "serieSeasonEpisodeTotalNumber")?.value)
          setEpisodesNumb(title?.metadata?.find((x) => x.fieldName === "serieSeasonEpisodeTotalNumber")?.value);
        setMetas(metas);
        setNewTitle(title?.name!);
      }
    });
  };

  const setMetas = (metas) => {
    //Adding blocs
    metas?.forEach((meta) => {
      if (meta.fieldType === "DbSynopsesMetadataValueInfos")
        setSynopInfos(meta.value.map((x) => ({ languageCode: x.languageCode, text: x.text, dataType: x.dataType })));
      if (meta.fieldType === "DbCastsMetadataValueInfos")
        setCastsInfos(
          meta.value.map((x) => ({ function: x.function, name: x.name, lastname: x.lastname, order: x.order }))
        );
      if (meta.fieldType === "DbTitlesMetadataValueInfos")
        setTitleInfos(meta.value.map((x) => ({ languageCode: x.languageCode, text: x.text })));
    });

    //fill fields
    setFields(
      produce((draft) => {
        metas
          ?.filter(
            (m) =>
              ![
                "titleType",
                "DbCastsMetadataValueInfos",
                "DbSynopsesMetadataValueInfos",
                "DbTitlesMetadataValueInfos",
              ].includes(m.fieldType!)
          )
          .forEach((meta) => {
            let match = draft.find((x) => x.tagName === meta.fieldName);
            if (match) {
              if (match.baseType === "DbStringMultiMetadataValueInfos") {
                match.value = {
                  $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStringMultiMetadataValueInfos, VDM.AirLab.Sdk.Core",
                  fieldLabel: meta.fieldLabel,
                  fieldType: meta.fieldType,
                  id: meta.id,
                  value: meta.value,
                };
              } else match.value = meta.value;
            }
          });
      })
    );
  };

  // Disabling conditions
  const isCreationReady = useMemo(() => {
    return !(pattern.length === 0 || selectedType === null);
  }, [pattern, selectedType]);

  const isTmbdSearchReady = () => {
    if (selectedType === null) return false;
    if (selectedType === "Movie") {
      if (pattern.length > 0) return true;
      else return false;
    }
    if (selectedType === "Season") {
      if (pattern.length > 0 && seasonNb.length > 0) return true;
      else return false;
    }
  };

  const enqueueCustomSnackbar = useCustomSnackbar();

  // Handlers
  const handleSubmit = useCallback(() => {
    if (metadataListFilled.length === 0 && context === "UPDATE")
      enqueueCustomSnackbar(t("alert:warning.noMetaFilled"), "warning");

    let request: any = null;
    if (internalTitle && metadataListFilled.length > 0 && formValid && context === "UPDATE") {
      let metas: any[] = [
        ...metadataListFilled.filter(
          (x) =>
            x.fieldType &&
            ![BaseMetadataType.DbSynopsesMetadataValueInfos, BaseMetadataType.DbCastsMetadataValueInfos].includes(
              x.fieldType
            )
        ),
      ];
      handleNotBaseMetadata(metas);
      request = titleService.UpdateTitle({
        ...internalTitle,
        name: newTitle !== internalTitle.name ? newTitle : internalTitle.name,
        titleCustomerId: customerId,
        metadata: metas,
      } as DbAssetObjectInfos);
    }
    if (context === "CREATE") {
      let metas: any[] = [
        {
          $type: "VDM.AirLab.Sdk.Core.Database.Types.DbStringMetadataValueInfos, VDM.AirLab.Sdk.Core",
          creationTime: moment().format("YYYY-MM-DD"),
          fieldLabel: "Catalog Type",
          fieldName: "titleType",
          fieldType: BaseMetadataType.DbStringMetadataValueInfos,
          value: selectedType,
          id: fields.find((x) => x.tagName === "titleType")?.id,
        },
      ];

      if (selectedType === "Season")
        metas.push(
          {
            $type: "VDM.AirLab.Sdk.Core.Database.Types.DbLongMetadataValueInfos, VDM.AirLab.Sdk.Core",
            creationTime: moment().format("YYYY-MM-DD"),
            fieldLabel: "Serie Season Number",
            fieldName: "serieSeasonNumber",
            fieldType: BaseMetadataType.DbLongMetadataValueInfos,
            value: parseInt(seasonNb),
            id: fields.find((x) => x.tagName === "serieSeasonNumber")?.id,
          },
          {
            $type: "VDM.AirLab.Sdk.Core.Database.Types.DbLongMetadataValueInfos, VDM.AirLab.Sdk.Core",
            creationTime: moment().format("YYYY-MM-DD"),
            fieldLabel: "Total Number of Episodes",
            fieldName: "serieSeasonEpisodeTotalNumber",
            fieldType: BaseMetadataType.DbLongMetadataValueInfos,
            value: parseInt(episodesNumb),
            id: fields.find((x) => x.tagName === "serieSeasonEpisodeTotalNumber")?.id,
          }
        );
      metas.push(...metadataListFilled);
      handleNotBaseMetadata(metas);

      let title = {
        $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAssetObjectInfos, VDM.AirLab.Sdk.Core",
        layer: ObjectLayer.Title,
        name: pattern,
        objectType: ObjectType.GraphSkeleton,
        status: ObjectStatus.New,
        platformId: cacheService.GetAppData()?.platformId as number,
        metadata: metas,
        titleCustomerId: customerId,
      };

      request = titleService.CreateTitle(title as DbAssetObjectInfos);
    }

    if (request) {
      request.then((response) => {
        if (response.success) {
          enqueueCustomSnackbar(t("alert:success.metadataUpdated"), "success");
        } else enqueueCustomSnackbar(t("alert:warning.oopsMessage") + response.message, "warning");
      });
    }
  }, [
    metadataListFilled,
    formValid,
    internalTitle,
    context,
    handleNotBaseMetadata,
    fields,
    pattern,
    customerId,
    selectedType,
    newTitle,
  ]);

  return (
    <Stack sx={{ overflowY: "auto", flex: "1 1 0%", px: 2 }}>
      <ListPageHeader mb={2}>
        <ListPageHeaderLeft
          title={
            context === "CREATE"
              ? t("component:listPageHeaderLeft.title.createTitle")
              : t("component:listPageHeaderLeft.title.updateTitle")
          }
        />
      </ListPageHeader>
      <Stack spacing={2}>
        <FormSection
          icon={<MoreHorizIcon />}
          defaultExpanded={true}
          sx={{ mt: 2 }}
          title={t("form:section.title.primaryTitle")}
        >
          <Grid container spacing={2}>
            <Grid size={6}>
              <AutocompleteSearchCatalog
                isForTitleCreation={true}
                onValueProposalClick={(value) => handleInternalTitlePick(value)}
                layer={ObjectLayer.Title}
                enableSearchCallback={true}
                query={pattern}
                onChangeQuery={(query) => {
                  setPattern(query);
                }}
                onClear={() => {
                  setPattern("");
                  if (context === "UPDATE") {
                    setContext("CREATE");
                    setTitleInfos([]);
                    setSynopInfos([]);
                    setCastsInfos([]);
                    setSelectedType(null);
                    setSeasonNb("");
                    setCustomerId("");
                    setEpisodesNumb("");
                    setMetadataListFilled([]);
                    setNewTitle("");
                    setInternalTitle(undefined);
                    setCurrentTitleInfos({ languageCode: null, text: null });
                    setCurrentSynopInfos({ dataType: null, languageCode: null, text: null });
                    setCurrentCastsInfos({ function: null, name: null, lastname: null, order: null });
                    setFields(
                      produce((draft) => {
                        draft.forEach((meta) => {
                          if (meta.value) meta.value = null;
                        });
                      })
                    );
                  }
                }}
              />
            </Grid>
            <Grid size={2}>
              <Autocomplete
                value={selectedType}
                onChange={(e, value) => setSelectedType(value)}
                options={titleTypes.map((x) => x.value)}
                sx={{ width: "100%" }}
                renderInput={(params) => (
                  <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("field:placeholder.selectTypeOfTitle")} />
                )}
              />
            </Grid>
            <Grid size={2}>
              {selectedType === "Season" && (
                <OutlinedInput
                  label={t("common:standard")}
                  size="medium"
                  type="number"
                  placeholder={t("field:placeholder.seasonNb")}
                  value={seasonNb}
                  onChange={(value) => setSeasonNb(value.target.value)}
                />
              )}
            </Grid>
            <Grid size={2} textAlign="end">
              <Button
                variant="outlined"
                size="medium"
                startIcon={<SearchIcon />}
                onClick={handleTmdbSearch}
                disabled={!isTmbdSearchReady()}
              >
                {t("button:searchInTmdb")}
              </Button>
            </Grid>
          </Grid>
        </FormSection>
        <FormSection icon={<SearchIcon />} title={t("form:section.title.baseMetadata")}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={5}>
              <Typography variant="body2" color="text.tier">
                {t("table:cellText.metadataField")}
              </Typography>
            </Grid>
            <Grid size={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:isMandatory")}
              </Typography>
            </Grid>
            <Grid size={5}>
              <Typography variant="body2" color="text.tier">
                {t("common:value")}
              </Typography>
            </Grid>
          </Grid>
          {context === "UPDATE" && internalTitle && (
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid size={5}>
                <Typography>{t("common:title")}</Typography>
              </Grid>
              <Grid size={2}>
                <TableCellBool value={false} />
              </Grid>
              <Grid size={5}>
                <OutlinedInput value={newTitle} onChange={(e) => setNewTitle(e.target.value)} sx={{ width: "100%" }} />
              </Grid>
            </Grid>
          )}
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={5}>
              <Typography>{customerCode} ID</Typography>
            </Grid>
            <Grid size={2}>
              <TableCellBool value={true} />
            </Grid>
            <Grid size={5}>
              <OutlinedInput
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                sx={{ width: "100%" }}
              />
            </Grid>
          </Grid>
          {selectedType === "Season" && (
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid size={5}>
                <Typography>{t("table:cellText.totalNumberOfEpisodes")}</Typography>
              </Grid>
              <Grid size={2}>
                <TableCellBool value={true} />
              </Grid>
              <Grid size={5}>
                <OutlinedInput
                  sx={{ width: "100%" }}
                  value={episodesNumb}
                  onChange={(e) => {
                    setEpisodesNumb(e.target.value);
                  }}
                  type="number"
                />
              </Grid>
            </Grid>
          )}
          <MetadataEditor
            byPassMetadataTagNames={["casts"]}
            objectIds={internalTitle?.id ? [internalTitle.id] : []}
            onMetadataChange={handleMetadataChanges}
            mandatorySwitch
            isCreation={context === "CREATE"}
          />
        </FormSection>
        <FormSection icon={<SearchIcon />} title={t("form:section.title.additionalTitlesInfos")}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:language")}
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography variant="body2" color="text.tier">
                {t("table:cellText.titleLocalizedText")}
              </Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="body2" color="text.tier">
                {t("common:action")}
              </Typography>
            </Grid>
          </Grid>

          {titleInfos.map((titleInfo) => (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={2}>
                <Autocomplete
                  options={fields.find((x) => x.baseType === "DbLanguageCodeMetadataValueInfos")?.values || []}
                  getOptionLabel={(option: any) => option.label + "(" + option.value + ")"}
                  value={titleInfo.languageCode}
                  onChange={(e, value) => {
                    setTitleInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(titleInfo));
                        if (index !== -1)
                          draft[index].languageCode = new PlatformLanguageCode({
                            externalId: value?.externalId,
                            id: value?.id,
                            label: value?.label,
                            value: value?.value,
                            valueObject: value?.valueObject,
                          });
                      })
                    );
                  }}
                  renderInput={(params) => <TextField sx={{ mt: -3 }} {...params} />}
                />
              </Grid>
              <Grid size={9}>
                <OutlinedInput
                  sx={{ width: "-webkit-fill-available" }}
                  value={titleInfo.text}
                  onChange={(e) =>
                    setTitleInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(titleInfo));
                        if (index !== -1) draft[index].text = e.target.value;
                      })
                    )
                  }
                />
              </Grid>
              <Grid size={1}>
                <IconButton
                  onClick={() =>
                    setTitleInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(titleInfo));
                        if (index !== -1) draft.splice(index, 1);
                      })
                    )
                  }
                >
                  <RemoveIcon sx={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={2}>
              <Autocomplete
                options={fields.find((x) => x.baseType === "DbLanguageCodeMetadataValueInfos")?.values || []}
                getOptionLabel={(option: any) => option.label + "(" + option.value + ")"}
                value={currentTitleInfos.languageCode || null}
                onChange={(e, value) => {
                  setCurrentTitleInfos(
                    produce((draft) => {
                      draft.languageCode = new PlatformLanguageCode({
                        externalId: value?.externalId,
                        id: value?.id,
                        label: value?.label,
                        value: value?.value,
                        valueObject: value?.valueObject,
                      });
                    })
                  );
                }}
                renderInput={(params) => <TextField sx={{ mt: -3 }} {...params} />}
              />
            </Grid>
            <Grid size={9}>
              <OutlinedInput
                sx={{ width: "-webkit-fill-available" }}
                value={currentTitleInfos.text || ""}
                onChange={(e) => {
                  setCurrentTitleInfos(
                    produce((draft) => {
                      draft.text = e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={1}>
              <IconButton
                onClick={() => {
                  setTitleInfos(
                    produce((draft) => {
                      draft.push(currentTitleInfos);
                    })
                  );
                  setCurrentTitleInfos(
                    produce((draft) => {
                      draft.text = null;
                      draft.languageCode = null;
                    })
                  );
                }}
                disabled={currentTitleInfos.languageCode === null || currentTitleInfos.text === null}
              >
                <AddIcon sx={{ width: "18px", height: "18px" }} />
              </IconButton>
            </Grid>
          </Grid>
        </FormSection>

        <FormSection icon={<SearchIcon />} title={t("form:section.title.synopsesInfo")}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:language")}
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography variant="body2" color="text.tier">
                {t("common:synopsis")}
              </Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="body2" color="text.tier">
                {t("common:action")}
              </Typography>
            </Grid>
          </Grid>
          {synopInfos.map((synopInfo) => (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={2}>
                <Autocomplete
                  options={fields.find((x) => x.baseType === "DbLanguageCodeMetadataValueInfos")?.values || []}
                  getOptionLabel={(option: any) => option.label + "(" + option.value + ")"}
                  value={synopInfo.languageCode}
                  onChange={(e, value) => {
                    setSynopInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(synopInfo));
                        if (index !== -1)
                          draft[index].languageCode = createLanguageCodeValue(value as PlatformLanguageCode);
                      })
                    );
                  }}
                  renderInput={(params) => <TextField sx={{ mt: -3 }} {...params} />}
                />
              </Grid>
              <Grid size={9}>
                <OutlinedInput
                  sx={{ width: "-webkit-fill-available" }}
                  value={synopInfo.text}
                  onChange={(e) =>
                    setSynopInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(synopInfo));
                        if (index !== -1) draft[index].text = e.target.value;
                      })
                    )
                  }
                />
              </Grid>
              <Grid size={1}>
                <IconButton
                  onClick={() =>
                    setSynopInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(synopInfo));
                        if (index !== -1) draft.splice(index, 1);
                      })
                    )
                  }
                >
                  <RemoveIcon sx={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={2}>
              <Autocomplete
                options={fields.find((x) => x.baseType === "DbLanguageCodeMetadataValueInfos")?.values || []}
                getOptionLabel={(option: any) => option.label + "(" + option.value + ")"}
                value={currentSynopInfos.languageCode || null}
                onChange={(e, value) => {
                  setCurrentSynopInfos(
                    produce((draft) => {
                      draft.languageCode = createLanguageCodeValue(value as PlatformLanguageCode);
                    })
                  );
                }}
                renderInput={(params) => <TextField sx={{ mt: -3 }} {...params} />}
              />
            </Grid>
            <Grid size={9}>
              <OutlinedInput
                sx={{ width: "-webkit-fill-available" }}
                value={currentSynopInfos.text || ""}
                onChange={(e) => {
                  setCurrentSynopInfos(
                    produce((draft) => {
                      draft.text = e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={1}>
              <IconButton
                onClick={() => {
                  setSynopInfos(
                    produce((draft) => {
                      draft.push(currentSynopInfos);
                    })
                  );
                  setCurrentSynopInfos(
                    produce((draft) => {
                      draft.text = null;
                      draft.languageCode = null;
                    })
                  );
                }}
                disabled={currentSynopInfos.languageCode === null || currentSynopInfos.text === null}
              >
                <AddIcon sx={{ width: "18px", height: "18px" }} />
              </IconButton>
            </Grid>
          </Grid>
        </FormSection>
        <FormSection icon={<SearchIcon />} title={t("form:section.title.castsInfo")}>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={3}>
              <Typography variant="body2" color="text.tier">
                {t("table:cellText.functionLabel")}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="body2" color="text.tier">
                {t("table:cellText.firstName")}
              </Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="body2" color="text.tier">
                {t("table:cellText.lastName")}
              </Typography>
            </Grid>
            <Grid size={2}>
              <Typography variant="body2" color="text.tier">
                {t("common:order")}
              </Typography>
            </Grid>
            <Grid size={1}>
              <Typography variant="body2" color="text.tier">
                {t("common:action")}
              </Typography>
            </Grid>
          </Grid>
          {castsInfos.map((castInfo) => (
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid size={3}>
                <OutlinedInput
                  sx={{ width: "-webkit-fill-available" }}
                  value={castInfo.function || ""}
                  onChange={(e) =>
                    setCastsInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(castInfo));
                        if (index !== -1) draft[index].function = e.target.value;
                      })
                    )
                  }
                />
              </Grid>
              <Grid size={3}>
                <OutlinedInput
                  sx={{ width: "-webkit-fill-available" }}
                  value={castInfo.name || ""}
                  onChange={(e) =>
                    setCastsInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(castInfo));
                        if (index !== -1) draft[index].name = e.target.value;
                      })
                    )
                  }
                />
              </Grid>
              <Grid size={3}>
                <OutlinedInput
                  sx={{ width: "-webkit-fill-available" }}
                  value={castInfo.lastname || ""}
                  onChange={(e) =>
                    setCastsInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(castInfo));
                        if (index !== -1) draft[index].lastname = e.target.value;
                      })
                    )
                  }
                />
              </Grid>
              <Grid size={2}>
                <OutlinedInput
                  sx={{ width: "-webkit-fill-available" }}
                  value={castInfo.order || null}
                  onChange={(e) =>
                    setCastsInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(castInfo));
                        if (index !== -1) draft[index].order = parseInt(e.target.value);
                      })
                    )
                  }
                />
              </Grid>
              <Grid size={1}>
                <IconButton
                  onClick={() =>
                    setCastsInfos(
                      produce((draft) => {
                        const index = draft.findIndex((x) => JSON.stringify(x) === JSON.stringify(castInfo));
                        if (index !== -1) draft.splice(index, 1);
                      })
                    )
                  }
                >
                  <RemoveIcon sx={{ width: "18px", height: "18px" }} />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid size={3}>
              <OutlinedInput
                sx={{ width: "-webkit-fill-available" }}
                value={currentCastsInfos.function || ""}
                onChange={(e) => {
                  setCurrentCastsInfos(
                    produce((draft) => {
                      draft.function = e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={3}>
              <OutlinedInput
                sx={{ width: "-webkit-fill-available" }}
                value={currentCastsInfos.name || ""}
                onChange={(e) => {
                  setCurrentCastsInfos(
                    produce((draft) => {
                      draft.name = e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={3}>
              <OutlinedInput
                sx={{ width: "-webkit-fill-available" }}
                value={currentCastsInfos.lastname || ""}
                onChange={(e) => {
                  setCurrentCastsInfos(
                    produce((draft) => {
                      draft.lastname = e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={2}>
              <OutlinedInput
                type="number"
                value={currentCastsInfos.order || ""}
                onChange={(e) => {
                  setCurrentCastsInfos(
                    produce((draft) => {
                      draft.order = parseInt(e.target.value);
                    })
                  );
                }}
              />
            </Grid>
            <Grid size={1}>
              <IconButton
                onClick={() => {
                  setCastsInfos(
                    produce((draft) => {
                      draft.push(currentCastsInfos);
                    })
                  );
                  setCurrentCastsInfos(
                    produce((draft) => {
                      draft.function = null;
                      draft.name = null;
                      draft.lastname = null;
                      draft.order = null;
                    })
                  );
                }}
                disabled={
                  currentCastsInfos.function === null ||
                  currentCastsInfos.name === null ||
                  currentCastsInfos.lastname === null ||
                  currentCastsInfos.order === null
                }
              >
                <AddIcon sx={{ width: "18px", height: "18px" }} />
              </IconButton>
            </Grid>
          </Grid>
        </FormSection>
      </Stack>
      <Stack alignItems="flex-end">
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          color="primary"
          onClick={handleConfirmClick}
          disabled={!isCreationReady}
        >
          {context === "CREATE"
            ? t("component:listPageHeaderLeft.title.createTitle")
            : t("component:listPageHeaderLeft.title.updateTitle")}
        </Button>
      </Stack>
    </Stack>
  );
}

export default InventoryCreateTitle;
